if (!customElements.get('cv-testimonial-feature')) {
  customElements.define('cv-testimonial-feature', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cv-testimonial-feature__track');
      this.slides = Array.from(this.querySelectorAll('.cv-testimonial-feature__slide'));
      this.controls = this.querySelector('.cv-testimonial-feature__controls');
      if (!this.track || !this.controls || this.slides.length < 2) return;

      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.current = this.querySelector('[data-current]');
      this.index = 0;
      this.previous.addEventListener('click', () => this.goTo(this.index - 1), options);
      this.next.addEventListener('click', () => this.goTo(this.index + 1), options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
        this.scheduleAutoplay();
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const targets = {
          ArrowLeft: this.index + (rtl ? 1 : -1),
          ArrowRight: this.index + (rtl ? -1 : 1),
          Home: 0,
          End: this.slides.length - 1
        };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.goTo(targets[event.key]);
      }, options);
      // Shopify dispatches this on the selected block, including offscreen slides.
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index !== -1) this.goTo(index, true);
      }, options);

      this.trackWidth = this.track.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.track.clientWidth !== this.trackWidth) {
          this.trackWidth = this.track.clientWidth;
          this.goTo(this.index, true);
        }
        this.updateHeight();
      });
      this.resizeObserver.observe(this.track);
      this.slides.forEach(slide => this.resizeObserver.observe(slide));
      this.controls.hidden = false;
      this.setAttribute('data-enhanced', '');
      this.sync();
      this.setupAutoplay(options);
    }

    setupAutoplay(options) {
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.dataset.designMode !== 'true';
      if (!this.autoplayEnabled) return;
      const seconds = Number(this.dataset.autoplayDelay) || 6;
      this.autoplayDelay = Math.max(2, Math.min(seconds, 20)) * 1000;
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.rotationControl = this.querySelector('[data-autoplay-control]');
      this.rotationButton = this.querySelector('[data-autoplay-toggle]');
      this.rotationIcon = this.querySelector('[data-autoplay-icon]');
      this.counter = this.querySelector('.cv-testimonial-feature__counter');
      this.userPaused = false;
      this.hovered = matchMedia('(hover: hover)').matches && this.matches(':hover');
      this.focused = this.contains(document.activeElement);
      this.pointerDown = false;
      this.inView = false;
      this.rotationButton.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.scheduleAutoplay();
      }, options);
      this.track.addEventListener('pointerdown', () => {
        this.pointerDown = true;
        this.scheduleAutoplay();
      }, options);
      const releasePointer = () => {
        if (!this.pointerDown) return;
        this.pointerDown = false;
        this.scheduleAutoplay();
      };
      document.addEventListener('pointerup', releasePointer, options);
      document.addEventListener('pointercancel', releasePointer, options);
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), options);
      this.motionPreference.addEventListener('change', () => this.scheduleAutoplay(), options);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.scheduleAutoplay();
      });
      this.visibilityObserver.observe(this);
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      clearTimeout(this.autoplayTimer);
      if (!this.autoplayEnabled) return;
      this.rotationControl.hidden = this.motionPreference.matches;
      this.rotationButton.setAttribute('aria-label', this.userPaused ? 'Resume autoplay' : 'Pause autoplay');
      this.rotationIcon.textContent = this.userPaused ? '▶' : 'Ⅱ';
      const running = this.isConnected && this.inView && !this.userPaused && !this.hovered &&
        !this.focused && !this.pointerDown && !document.hidden && !this.motionPreference.matches;
      this.counter.setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running) return;
      this.autoplayTimer = setTimeout(() => {
        const next = (this.index + 1) % this.slides.length;
        this.goTo(next, next === 0);
      }, this.autoplayDelay);
    }

    goTo(index, instant = false) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      const trackBox = this.track.getBoundingClientRect();
      const slideBox = this.slides[this.index].getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const delta = rtl ? slideBox.right - trackBox.right : slideBox.left - trackBox.left;
      const immediate = instant || matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: this.track.scrollLeft + delta,
        behavior: immediate ? 'instant' : 'smooth'
      });
      // During smooth scrolling, sync() updates the count as the next slide comes into view.
      if (immediate || Math.abs(delta) < 1) this.updateControls();
      this.scheduleAutoplay();
    }

    sync() {
      const trackBox = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const edge = rtl ? 'right' : 'left';
      let closest = Infinity;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect()[edge] - trackBox[edge]);
        if (distance < closest) {
          closest = distance;
          this.index = index;
        }
      });
      this.updateControls();
    }

    updateControls() {
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.slides.length - 1;
      const value = String(this.index + 1);
      if (this.current.textContent !== value) this.current.textContent = value;
      this.updateHeight();
    }

    updateHeight() {
      if (matchMedia('(max-width: 749px)').matches) {
        // Slides keep their natural height; only the mobile viewport follows the active slide.
        this.track.style.height = `${Math.ceil(this.slides[this.index].getBoundingClientRect().height)}px`;
      } else {
        this.track.style.removeProperty('height');
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.autoplayTimer);
      cancelAnimationFrame(this.frame);
      this.track?.style.removeProperty('height');
    }
  });
}
